import { CheckBox } from '@mui/icons-material';
import { Container, Snackbar, Alert, Paper, Dialog, DialogContentText, Box, Button, Typography, Backdrop, CircularProgress, TableContainer, Table, TablePagination, TableBody, TableHead, TableCell, TableRow, TextField, InputAdornment, MenuItem, DialogTitle, DialogActions, DialogContent, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import SearchBox from '../../components/SearchBox';
import fetchData from '../../components/fetchData';
import { auth } from '../../config/firebase';
import { getAuth } from 'firebase/auth';
import getIdToken from '../../components/getIdToken';
import moment from 'moment';

const Plans = () => {
    const user = auth.currentUser;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState('');
    const [currentRow, setCurrentRow] = useState(null);
    const [page, setPage] = useState('');
    const rowsPerPage = 10;
    const [planPaginatedList, setPlanPaginatedList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [errors, setErrors] = useState('');

    const jsonPlaceholder = `{
    "monitors_allowed":8,
    "monitor_interval":30
}`;

    const handleChangePage = async (event, newPage) => {
        setPage(newPage);
        const queryParams = new URLSearchParams();
        queryParams.set('page', newPage + 1);
        navigate(`/dashboard/plans?${queryParams.toString()}`);
    };

    const handleChangeRowsPerPage = () => {
        //
    }

    const openAddDialog = () => {
        setCurrentRow({ active: undefined, subscription_Duration: "", regular_price: "", discounted_price: "", domainNumber: "", credit_duration: "", credits: "", promotion_type: "" });
        setDialogOpen(true);
    }

    const handleDialogClose = () => {
        setCurrentRow(null);
        setDialogOpen(false);
        setIsEditing(false);
        setErrors({});
    }

    const openDeleteDialog = (type, id) => {
        const currentRow = planPaginatedList.find(plan => plan.type === type && plan.id === id);
        setCurrentRow(currentRow);
        setDeleteDialogOpen(true);
    }

    const handleDeleteDialogClose = () => {
        setCurrentRow(null);
        setDeleteDialogOpen(false);
    }

    const handleEdit = (type, id) => {
        const currentRow = planPaginatedList.find(plan => plan.type === type && plan.id === id);
        // Display tags (Recommended / Popular) are stored inside `features`; lift them
        // onto the form row so the checkboxes reflect the saved state.
        const f = (currentRow && typeof currentRow.features === 'object' && currentRow.features) || {};
        setCurrentRow({ ...currentRow, recommended: !!f.recommended, popular: !!f.popular });
        setDialogOpen(true);
        setIsEditing(true);
    }

    const handleDelete = async (planId) => {
        try {
            let apiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/v1/plan/${planId}?type=${currentRow.type.toLowerCase()}`

            await fetchData({
                url: apiUrl,
                method: 'DELETE',
            });
            setSnackbarMessage("Delete successfully!");
            setSnackbarOpen(true);
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.log(error);
            setSnackbarMessage("Failed to delete. Please try again.");
            setSnackbarOpen(true);
        }
        setCurrentRow(null);
        setDeleteDialogOpen(false);
    }

    function isValidJSONObject(str) {
        const input = String(str || '').trim();
        if (input === '') return true; // Empty input is considered valid

        try {
            const parsed = JSON.parse(str);
            return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
        } catch (e) {
            return false;
        }
    }

    const handleSavePlan = async (planId) => {
        let errs = {};
        let hasError = false;

        if (!currentRow.type) {
            errs.type = "Type must not be empty.";
            hasError = true;
        }
        if (currentRow.active === undefined || currentRow.active === null) {
            errs.status = "Status must not be empty.";
            hasError = true;
        }
        if (!currentRow.name) {
            errs.name = "Name must not be empty.";
            hasError = true;
        }

        // Validation for subscription and credit types
        if (currentRow.type === "subscription" || currentRow.type === "credit") {
            if (!currentRow.regular_price && currentRow.regular_price !== 0) {
                errs.regular_price = "Regular Price must not be empty.";
                hasError = true;
            } if (currentRow.regular_price < 0) {
                errs.regular_price = "Regular Price must be larger than 0.";
                hasError = true;
            } else if (isNaN(parseFloat(currentRow.regular_price))) {
                errs.regular_price = "Regular Price must be float.";
                hasError = true;
            }
            if (currentRow.discounted_price < 0) {
                errs.discounted_price = "Discounted Price must be larger than 0.";
                hasError = true;
            } else if (currentRow.discounted_price && isNaN(parseFloat(currentRow.discounted_price))) {
                errs.discounted_price = "Discounted Price must be float.";
                hasError = true;
            }
            if (currentRow.regular_price && currentRow.discounted_price && parseFloat(currentRow.regular_price) <= parseFloat(currentRow.discounted_price)) {
                errs.discounted_price = "Discounted Price must be smaller than regular price."
                hasError = true;
            }
        }

        // Credit duration validation (for all types)
        if (!currentRow.credit_duration && currentRow.credit_duration !== 0) {
            errs.credit_duration = "Credit expiration must not be empty.";
            hasError = true;
        } else if (currentRow.credit_duration < 0) {
            errs.credit_duration = "Credits expiration must be larger than 0.";
            hasError = true;
        } else if (!Number.isInteger(Number(currentRow.credit_duration))) {
            errs.credit_duration = "Credit expiration must be an integer.";
            hasError = true;
        }

        // Credits validation (for all types)
        if (!currentRow.credits && currentRow.credits !== 0) {
            errs.credits = "Credits must not be empty.";
            hasError = true;
        } else if (currentRow.credits < 0) {
            errs.credits = "Credits must be larger than 0.";
            hasError = true;
        } else if (!Number.isInteger(Number(currentRow.credits))) {
            errs.credits = "Credits must be an integer.";
            hasError = true;
        }

        // Subscription-specific validation
        if (currentRow.type === "subscription") {
            if (!currentRow.subscription_duration) {
                errs.subscription_duration = "Duration must not be empty.";
                hasError = true;
            }

            const featuresInput =
                typeof currentRow.features === "string" ? currentRow.features.trim() : "";

            if (!isValidJSONObject(featuresInput)) {
                errs.features = "Invalid JSON.";
                hasError = true;
            }
        }

        // Promotion-specific validation
        if (currentRow.type === "promotion") {
            if (!currentRow.promotion_type) {
                errs.promotion_type = "Promotion type must not be empty.";
                hasError = true;
            }
        }

        setErrors(errs);

        if (Object.keys(errs).length) return;
        if (hasError) return;

        try {
            let payload = {
                name: currentRow.name,
                type: currentRow.type,
                active: currentRow.active,
                credit_duration: parseInt(currentRow.credit_duration, 10),
                credits: parseInt(currentRow.credits, 10),
            }

            // Display tags (Recommended / Popular) live inside `features`, so the
            // backend persists & serves them with no schema change.
            const baseFeatures = typeof currentRow.features === "string" && currentRow.features.trim() !== ""
                ? JSON.parse(currentRow.features)
                : (typeof currentRow.features === "object" && currentRow.features !== null ? { ...currentRow.features } : {});
            const featuresWithTags = { ...baseFeatures, recommended: !!currentRow.recommended, popular: !!currentRow.popular };

            // Add subscription-specific fields
            if (currentRow.type === "subscription") {
                payload = {
                    ...payload,
                    regular_price: parseFloat(currentRow.regular_price),
                    discounted_price: parseFloat(currentRow.discounted_price),
                    subscription_duration: currentRow.subscription_duration,
                    features: featuresWithTags,
                }
            }

            // Add credit-specific fields
            if (currentRow.type === "credit") {
                payload = {
                    ...payload,
                    regular_price: parseFloat(currentRow.regular_price),
                    discounted_price: parseFloat(currentRow.discounted_price),
                    features: featuresWithTags,
                }
            }

            // Add promotion-specific fields
            if (currentRow.type === "promotion") {
                payload = {
                    ...payload,
                    promotion_type: currentRow.promotion_type,
                }
            }

            const apiUrl = planId
                ? `${process.env.REACT_APP_API_BASE_URL}xeo/v1/plan/${planId}`
                : `${process.env.REACT_APP_API_BASE_URL}xeo/v1/plan`


            await fetchData({
                url: apiUrl,
                method: planId ? 'PUT' : 'POST',
                body: payload
            });

            setDialogOpen(false);
            setSnackbarMessage("Save successfully!");
            setSnackbarOpen(true);
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.log(error);
            setSnackbarMessage("Failed to save. Please try again.");
        }
    }

    useEffect(() => {
        const fetchPlans = async () => {
            setLoading(true);
            try {
                if (!user) return;
                const pageParam = parseInt(searchParams.get('page')) || 1;
                const currentPage = pageParam - 1;
                setPage(currentPage);

                const plansRes = await fetchData({
                    url: `${process.env.REACT_APP_API_BASE_URL}xeo/v1/plans`,
                });
                const plans = plansRes.plans;
                plans.forEach((plan, i) => {
                    if (!plan.type) return;
                    plans[i].type = plans[i].type.toLowerCase()
                })

                console.log("plan data:", plans);
                const startIndex = currentPage * rowsPerPage;
                const endIndex = Math.min(startIndex + rowsPerPage, plans.length);
                const paginatedItems = plans.slice(startIndex, endIndex);
                setPlanPaginatedList(paginatedItems);
                setTotalCount(plans.length);
            } catch {
                console.log("Failed to fetch plans.")
            }
            setLoading(false);
        }
        fetchPlans();
    }, [location.search, navigate]);


    return (
        <Container maxWidth={false} sx={{ width: '100%', padding: 0, paddingBottom: '5px' }}>
            <Box alignItems="center" sx={{ paddingTop: '10px' }}>
                <Box xs={4.5} display="flex" justifyContent="space-between">
                    <Typography variant='h5' gutterBottom paddingLeft={"6px"}>
                        Plans
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={openAddDialog}
                    >
                        Add
                    </Button>
                </Box>
            </Box>
            {loading ? (
                <Backdrop open={true} style={{ color: '#fff', zIndex: 1 }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            ) : (
                <TableContainer component={Paper} style={{ marginTop: 20 }}>
                    <Table>
                        <TableHead>
                            <TableRow style={{ fontWeight: 'bold' }}>
                                <TableCell style={{ fontWeight: 'bold' }}>Plan ID</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Plan Type</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Promotion Type</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Plan Name</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Regular Price</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Discounted Price</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Subscription Duration</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Credits</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Credit Expiration</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}># of Domain Check</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Creation Time</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {planPaginatedList?.map((plan) => {
                                return (
                                    <TableRow key={plan.type + plan.id}>
                                        <TableCell>{plan.id || "-"}</TableCell>
                                        <TableCell>{plan.type || "-"}</TableCell>
                                        <TableCell>{plan.promotion_type || "-"}</TableCell>
                                        <TableCell>{plan.name || "-"}</TableCell>
                                        <TableCell>{plan.active === true ? 'Active' : 'Inactive'}</TableCell>
                                        <TableCell>{plan.regular_price || "-"}</TableCell>
                                        <TableCell>{plan.discounted_price || "-"}</TableCell>
                                        <TableCell>{plan.subscription_duration || "-"}</TableCell>
                                        <TableCell>{plan.credits || "-"}</TableCell>
                                        <TableCell>{plan.credit_duration ? plan.credit_duration + ' days' : "-"}</TableCell>
                                        <TableCell>{plan.features?.monitors_allowed || "-"}</TableCell>
                                        <TableCell>{moment(plan.created).format('YYYY-MM-DD HH:mm') || "-"}</TableCell>
                                        <TableCell align="center">
                                            <Button
                                                size="small"
                                                color="success"
                                                onClick={() => handleEdit(plan.type, plan.id)}
                                                startIcon={<Edit />}
                                                style={{ marginLeft: 10, fontWeight: 'bold' }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                onClick={() => openDeleteDialog(plan.type, plan.id)}
                                                startIcon={<Delete />}
                                                style={{ marginLeft: 10, fontWeight: 'bold' }}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>{currentRow?.id ? "Edit Plan" : "Add Plan"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Plan Type"
                        select
                        value={currentRow?.type || ""}
                        onChange={e => {
                            setCurrentRow({ ...currentRow, type: e.target.value });
                            setErrors(prev => ({ ...prev, type: "" }));
                        }}
                        disabled={isEditing}
                        required
                        fullWidth
                        margin='normal'
                        error={!!errors.type}
                        helperText={errors.type}
                    >
                        <MenuItem value="subscription">Subscription</MenuItem>
                        <MenuItem value="credit">Credit</MenuItem>
                        <MenuItem value="promotion">Promotion</MenuItem>
                    </TextField>

                    {/* Common fields for all types */}
                    <TextField
                        label="Status"
                        select
                        value={currentRow?.active === true
                            ? 'true'
                            : currentRow?.active === false
                                ? 'false'
                                : ''}
                        onChange={e => {
                            const v = e.target.value === "true";
                            setCurrentRow(prev => ({ ...prev, active: v }));
                            setErrors(prev => ({ ...prev, status: "" }));
                        }}
                        required
                        fullWidth
                        margin='normal'
                        error={!!errors.status}
                        helperText={errors.status}
                    >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </TextField>

                    <TextField
                        label="Plan Name"
                        value={currentRow?.name || ""}
                        onChange={e => {
                            setCurrentRow({ ...currentRow, name: e.target.value });
                            setErrors(prev => ({ ...prev, name: "" }));
                        }}
                        required
                        fullWidth
                        margin='normal'
                        error={!!errors.name}
                        helperText={errors.name}
                    />

                    {/* Display tags — Recommended / Popular. Stored in `features`;
                        shown as chips on the user-facing plan/credit cards. */}
                    {(currentRow?.type === 'subscription' || currentRow?.type === 'credit') && (
                        <Box sx={{ mt: 1, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Display tag</Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={!!currentRow?.recommended} onChange={(e) => setCurrentRow({ ...currentRow, recommended: e.target.checked })} />}
                                    label="Recommended"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={!!currentRow?.popular} onChange={(e) => setCurrentRow({ ...currentRow, popular: e.target.checked })} />}
                                    label="Popular"
                                />
                            </FormGroup>
                        </Box>
                    )}

                    {/* Subscription-specific fields */}
                    {currentRow?.type === 'subscription' && (
                        <>
                            <TextField
                                label="Subscription Duration"
                                select
                                value={currentRow?.subscription_duration || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, subscription_duration: e.target.value });
                                    setErrors(prev => ({ ...prev, subscription_duration: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.subscription_duration}
                                helperText={errors.subscription_duration}
                            >
                                <MenuItem value="month">Monthly</MenuItem>
                                <MenuItem value="year">Yearly</MenuItem>
                            </TextField>
                            <TextField
                                label="Regular Price"
                                value={
                                    currentRow?.regular_price
                                        ? currentRow.regular_price
                                        : currentRow?.id
                                            ? "0"
                                            : ""
                                }
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, regular_price: e.target.value });
                                    setErrors(prev => ({ ...prev, regular_price: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.regular_price}
                                helperText={errors.regular_price}
                            />
                            <TextField
                                label="Discounted Price (Optional)"
                                value={currentRow?.discounted_price || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, discounted_price: e.target.value });
                                    setErrors(prev => ({ ...prev, discounted_price: "" }));
                                }}
                                fullWidth
                                margin='normal'
                                error={!!errors.discounted_price}
                                helperText={errors.discounted_price}
                            />
                            <TextField
                                label="Credits"
                                value={currentRow?.credits || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, credits: e.target.value });
                                    setErrors(prev => ({ ...prev, credits: "" }));
                                }
                                }
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.credits}
                                helperText={errors.credits}
                            />
                            <TextField
                                label="Credit Expiration"
                                value={currentRow?.credit_duration || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, credit_duration: e.target.value });
                                    setErrors(prev => ({ ...prev, credit_duration: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.credit_duration}
                                helperText={errors.credit_duration}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                                }}
                            />
                            <TextField
                                label="Features (Optional)"
                                value={
                                    typeof currentRow?.features === 'object' && currentRow.features !== null
                                        ? JSON.stringify(currentRow.features, null, 2)
                                        : currentRow?.features || ""
                                }
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, features: e.target.value });
                                    setErrors(prev => ({ ...prev, features: "" }));
                                }}
                                fullWidth
                                placeholder={jsonPlaceholder}
                                margin='normal'
                                multiline
                                rows={4}
                                error={!!errors.features}
                                helperText={
                                    errors.features
                                        ? errors.features
                                        : 'Sample JSON: { "monitors_allowed":8, "monitor_interval":30 }'
                                }
                            />
                        </>
                    )}

                    {/* Credit-specific fields */}
                    {currentRow?.type === 'credit' && (
                        <>
                            <TextField
                                label="Regular Price"
                                value={
                                    currentRow?.regular_price
                                        ? currentRow.regular_price
                                        : currentRow?.id
                                            ? "0"
                                            : ""
                                }
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, regular_price: e.target.value });
                                    setErrors(prev => ({ ...prev, regular_price: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.regular_price}
                                helperText={errors.regular_price}
                            />
                            <TextField
                                label="Discounted Price (Optional)"
                                value={currentRow?.discounted_price || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, discounted_price: e.target.value });
                                    setErrors(prev => ({ ...prev, discounted_price: "" }));
                                }}
                                fullWidth
                                margin='normal'
                                error={!!errors.discounted_price}
                                helperText={errors.discounted_price}
                            />
                            <TextField
                                label="Credits"
                                value={currentRow?.credits || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, credits: e.target.value });
                                    setErrors(prev => ({ ...prev, credits: "" }));
                                }
                                }
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.credits}
                                helperText={errors.credits}
                            />
                            <TextField
                                label="Credit Expiration"
                                value={currentRow?.credit_duration || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, credit_duration: e.target.value });
                                    setErrors(prev => ({ ...prev, credit_duration: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.credit_duration}
                                helperText={errors.credit_duration}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                                }}
                            />
                        </>
                    )}

                    {/* Promotion-specific fields */}
                    {currentRow?.type === 'promotion' && (
                        <>
                            <TextField
                                label="Promotion Type"
                                value={currentRow?.promotion_type || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, promotion_type: e.target.value });
                                    setErrors(prev => ({ ...prev, promotion_type: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.promotion_type}
                                helperText={errors.promotion_type || "e.g., Sign-up Bonus, Referral Reward, Seasonal"}
                                placeholder="Enter promotion type"
                            />
                            <TextField
                                label="Credits"
                                value={currentRow?.credits || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, credits: e.target.value });
                                    setErrors(prev => ({ ...prev, credits: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.credits}
                                helperText={errors.credits}
                            />
                            <TextField
                                label="Credit Expiration"
                                value={currentRow?.credit_duration || ""}
                                onChange={e => {
                                    setCurrentRow({ ...currentRow, credit_duration: e.target.value });
                                    setErrors(prev => ({ ...prev, credit_duration: "" }));
                                }}
                                required
                                fullWidth
                                margin='normal'
                                error={!!errors.credit_duration}
                                helperText={errors.credit_duration}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                                }}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25, paddingTop: 0 }}>
                    <Button onClick={handleDialogClose} color="secondary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Cancel
                    </Button>
                    <Button onClick={() => handleSavePlan(currentRow?.id)} color="primary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ marginTop: 2 }}>
                        Are you sure you want to delete this plan?
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25, paddingTop: 0 }}>
                    <Button onClick={handleDeleteDialogClose} color="secondary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Cancel
                    </Button>
                    <Button onClick={() => { handleDelete(currentRow?.id) }} color="primary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <div style={{ flexGrow: 1 }} />
            <TablePagination
                component="div"
                count={totalCount}
                page={Math.max(0, page)}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[]}
                style={{ paddingBottom: '20px', paddingTop: '10px', overflow: 'hidden' }}
            />
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarMessage.includes('successfully') ? 'success' : 'error'} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    )
}

export default Plans;